import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { MapboxStyle } from '../components/common/lebanon-map/lebanon-map-base-mapbox';

const MAP_STYLE_STORAGE_KEY = 'parceltracer_map_style';

export const MAP_STYLE_OPTIONS: { value: MapboxStyle; label: string }[] = [
  { value: 'streets-v12', label: 'Streets' },
  { value: 'light-v11', label: 'Light' },
  { value: 'dark-v11', label: 'Dark' },
  { value: 'outdoors-v12', label: 'Outdoors' },
  { value: 'satellite-v9', label: 'Satellite' },
  { value: 'satellite-streets-v12', label: 'Satellite Streets' },
  { value: 'navigation-day-v1', label: 'Navigation' },
];

interface MapStyleContextType {
  mapStyle: MapboxStyle;
  setMapStyle: (style: MapboxStyle) => void;
  options: typeof MAP_STYLE_OPTIONS;
}

const MapStyleContext = createContext<MapStyleContextType | undefined>(undefined);

const getInitialMapStyle = (): MapboxStyle => {
  try {
    const saved = localStorage.getItem(MAP_STYLE_STORAGE_KEY);
    if (saved && MAP_STYLE_OPTIONS.some(opt => opt.value === saved)) {
      return saved as MapboxStyle;
    }
  } catch {
    // localStorage not available
  }
  return 'streets-v12';
};

interface MapStyleProviderProps {
  children: ReactNode;
}

export function MapStyleProvider({ children }: MapStyleProviderProps) {
  const [mapStyle, setMapStyleState] = useState<MapboxStyle>(getInitialMapStyle);

  const setMapStyle = useCallback((style: MapboxStyle) => {
    setMapStyleState(style);
    try {
      localStorage.setItem(MAP_STYLE_STORAGE_KEY, style);
    } catch {
      // localStorage not available
    }
  }, []);

  return (
    <MapStyleContext.Provider value={{ mapStyle, setMapStyle, options: MAP_STYLE_OPTIONS }}>
      {children}
    </MapStyleContext.Provider>
  );
}

export function useMapStyle(): MapStyleContextType {
  const context = useContext(MapStyleContext);
  if (!context) {
    throw new Error('useMapStyle must be used within a MapStyleProvider');
  }
  return context;
}

export { MapStyleContext };

